import * as THREE from 'three'

// ── State ────────────────────────────────────────────────────────────────────

// Subtree bone sets — cached per blend-bone name, invalidated on scene change.
const _subtreeBoneCache  = new Map()   // boneName → Set<boneName> | null

// All bones discovered from SkinnedMesh.skeleton.bones — cached once per scene.
const _boneRefs          = new Map()   // boneName → Bone object

// Pose captures for blending — quaternions only (Unreal-like data structure).
const _basePose          = new Map()   // boneName → THREE.Quaternion
const _layerPose         = new Map()   // boneName → THREE.Quaternion

let _config              = null        // { blendBone, blendWeight, blendClips: Map<clipName, weight> }
let _lastScene           = null
const _warned            = new Set()

// Time persistence — applyWeights stops blend clips, resetting time to 0.
// We save/restore time so they play continuously across frames.
const _blendClipTimes    = new Map()   // clipName → number

export const layeredBoneBlendRuntime = {
  setConfig(config) {
    _config = config
  },

  // Called AFTER applyWeights(), BEFORE mixer.update(delta).
  // Blend clips are stopped by applyWeights (they're not in the weights map).
  // We resume playback and restore time so they play continuously.
  preUpdate(mixer, actions) {
    if (!_config?.blendClips?.size || _config.blendWeight <= 0) return
    
    for (const [clipName] of _config.blendClips) {
      const action = actions[clipName]
      if (!action) continue
      
      if (!action.isRunning()) {
        action.play()
        // Restore time — applyWeights.stop() reset it to 0
        const savedTime = _blendClipTimes.get(clipName) ?? 0
        action.time = savedTime
      }
    }
  },

  // Unreal-like two-pose blending:
  // 1. Evaluate base pose
  // 2. Evaluate layer pose
  // 3. Blend per-bone: subtree uses SLERP, non-subtree uses base only
  postUpdate(scene, mixer, actions) {
    try {
      if (!_config) return
      const { blendBone, blendWeight, blendClips } = _config
      if (!blendBone || !blendClips?.size || blendWeight <= 0) return

      // Invalidate caches on scene change (avatar reload).
      if (_lastScene !== scene) {
        _lastScene = scene
        _subtreeBoneCache.clear()
        _boneRefs.clear()
        _basePose.clear()
        _layerPose.clear()
        _blendClipTimes.clear()
        _warned.clear()
        _discoverBones(scene)
      }

      // Get subtree for this blend bone.
      const subtree = _getSubtreeBones(scene, blendBone)
      if (!subtree) return

      // ── PASS 1: Capture base pose (only base active, no layer) ──────────────────

      // Stop and hide all blend layer clips.
      for (const [clipName] of blendClips) {
        const a = actions[clipName]
        if (a && a.isRunning()) {
          a.setEffectiveWeight(0)
        }
      }

      // Evaluate base only — all other animations play normally.
      mixer.update(0)

      // Capture base pose for all bones.
      for (const [boneName, bone] of _boneRefs) {
        let q = _basePose.get(boneName)
        if (!q) { q = new THREE.Quaternion(); _basePose.set(boneName, q) }
        q.copy(bone.quaternion)
      }

      // ── PASS 2: Capture layer pose (only layer active, no base) ──────────────────

      // Zero all normal actions.
      for (const clipName in actions) {
        const a = actions[clipName]
        if (a && a.isRunning() && !blendClips.has(clipName)) {
          a.setEffectiveWeight(0)
        }
      }

      // Activate only blend layer clips.
      for (const [clipName, weight] of blendClips) {
        const a = actions[clipName]
        if (!a) continue
        if (weight > 0) {
          a.setEffectiveWeight(weight)
          if (!a.isRunning()) a.play()
        }
      }

      // Evaluate layer only.
      mixer.update(0)

      // Capture layer pose for all bones.
      for (const [boneName, bone] of _boneRefs) {
        let q = _layerPose.get(boneName)
        if (!q) { q = new THREE.Quaternion(); _layerPose.set(boneName, q) }
        q.copy(bone.quaternion)
      }

      // ── BLEND: Apply per-bone blend using blendWeight ───────────────────────────

      for (const boneName of _boneRefs.keys()) {
        const bone = _boneRefs.get(boneName)
        if (!bone) continue

        const baseQ = _basePose.get(boneName)
        const layerQ = _layerPose.get(boneName)
        if (!baseQ || !layerQ) continue

        if (subtree.has(boneName)) {
          // Subtree bone: blend between base and layer
          bone.quaternion.slerpQuaternions(baseQ, layerQ, blendWeight)
        } else {
          // Non-subtree bone: use base pose only
          bone.quaternion.copy(baseQ)
        }
      }

      // ── Restore normal animation state ──────────────────────────────────────────

      // Reactivate all normal animations (so next frame renders normally).
      for (const clipName in actions) {
        const a = actions[clipName]
        if (a && !blendClips.has(clipName)) {
          a.setEffectiveWeight(1)
        }
      }

      // Deactivate blend clips (keep them running in background for time continuity).
      for (const [clipName] of blendClips) {
        const a = actions[clipName]
        if (a) {
          a.setEffectiveWeight(0)
          _blendClipTimes.set(clipName, a.time)
        }
      }
    } catch (err) {
      console.error(`[LBB] FATAL ERROR in postUpdate:`, err)
    }
  },
}

if (typeof window !== 'undefined') {
  window.__LBB_RUNTIME__ = layeredBoneBlendRuntime
}

// ── Helper Functions ─────────────────────────────────────────────────────────

function _discoverBones(scene) {
  try {
    scene.traverse(obj => {
      if (obj.isSkinnedMesh && obj.skeleton) {
        for (const bone of obj.skeleton.bones) {
          if (bone.name && !_boneRefs.has(bone.name)) {
            _boneRefs.set(bone.name, bone)
          }
        }
      }
    })
  } catch (err) {
    console.error(`[LBB] ERROR in _discoverBones:`, err)
  }
}

function _getSubtreeBones(scene, boneName) {
  try {
    if (_subtreeBoneCache.has(boneName)) {
      const cached = _subtreeBoneCache.get(boneName)
      if (cached) return cached
    }

    // Find the bone by name AND populate _boneRefs as we search.
    let root = _boneRefs.get(boneName)
    if (!root) {
      // Search skeletons and populate _boneRefs on the fly.
      scene.traverse(obj => {
        if (obj.isSkinnedMesh && obj.skeleton) {
          for (const bone of obj.skeleton.bones) {
            // Cache every bone we find
            if (bone.name && !_boneRefs.has(bone.name)) {
              _boneRefs.set(bone.name, bone)
            }
            // Check if this is our target
            if (bone.name === boneName) {
              root = bone
            }
          }
        }
      })
    }

    if (!root) {
      _subtreeBoneCache.set(boneName, null)
      return null
    }

    // Collect all descendants of this bone.
    const subtree = new Set()
    root.traverse(obj => {
      if (obj.name) subtree.add(obj.name)
    })

    _subtreeBoneCache.set(boneName, subtree)
    return subtree
  } catch (err) {
    console.error(`[LBB] ERROR in _getSubtreeBones for "${boneName}":`, err)
    return null
  }
}
